import {once} from '@packages/utilities/memo';
import {identity} from '@packages/utilities/fp/utils';
import * as React from 'react';
import {generateGuid} from '@packages/utilities/guid';
import {EElement, EText} from '@packages/systems/wfdl-ast';

import {
  Component,
  getMeta,
  Result,
  TypeImplementation,
  Types,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  type ContextualExpressionExpr,
  stripAnn,
  getAnn,
  ContextualExpressions,
} from '@packages/utilities/contextual-expression';

import {
  updateExpressionAction,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {
  iconName,
  pluginName,
  namesEqual,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import {useTheme} from '@packages/utilities/styled';
import TextInput from '@packages/systems/design-system/TextInput';
import TextArea from '@packages/systems/design-system/Textarea';
import Hitarea from '@packages/systems/design-system/Hitarea';

const constantNull = (_: ContextualExpressionExpr) => null;

const getStringFromElement = ContextualExpressions.matchExpression<
  string | null
>({
  Element: (el) =>
    namesEqual(el.type, stringType())
      ? ContextualExpressions.matchExpression<string | null>({
          Text: identity,
          _: constantNull,
        })(stripAnn(el.data))
      : null,
  _: constantNull,
});

const defaultTextMeta: Record<string, any> = {};
const stringType = once(() => {
  return qualify(pluginName('Basic'), Component.name('String'));
});
export const ElementPrimitiveType = TypeImplementation.Custom({
  parseValue: (_) =>
    Result.Error('Cannot use Literal expressions to create Element values.'),
  render: function ElementPrimitive({expr, dispatch}) {
    const theme = useTheme();
    const {env} = useExpressionEditorImplementationContext();
    const type = getAnn(expr)?.type;
    const maybeMeta = type ? getMeta(type) : null;
    const meta = maybeMeta ? maybeMeta : defaultTextMeta;

    const elementString = getStringFromElement(stripAnn(expr));
    const [value, setValue] = React.useState(elementString);

    React.useEffect(() => {
      const nextElementString = getStringFromElement(stripAnn(expr));
      setValue(nextElementString);
    }, [expr]);

    const onFocus = React.useCallback(() => {
      env.transaction.begin();
    }, [env.transaction]);

    const onBlur = React.useCallback(() => {
      env.transaction.commit();
    }, [env.transaction]);

    const onChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        dispatch(
          updateExpressionAction(
            EElement({
              data: EText(e.target.value),
              type: stringType(),
              id: generateGuid(),
            })
          )
        );
      },
      [dispatch]
    );

    if (value === null) {
      return null;
    }

    if (meta.multiLine) {
      const placeholderStyles = {
        padding: theme.gutters.base,
      } as const;
      const style = {
        minHeight: theme.gutters.base * 9,
        padding: theme.gutters.base,
        zIndex: undefined,
      } as const;
      return (
        <TextArea
          data-automation-id={`${
            type ? Types.generateAutomationIds(type) : 'element-primitive'
          }`}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
          placeholderStyles={placeholderStyles}
          placeholderValue={meta.placeholder}
          rows={5}
          selectAll
          style={style}
          text={value}
        />
      );
    }

    return (
      <Hitarea>
        <TextInput
          data-automation-id={`${
            type ? Types.generateAutomationIds(type) : 'element-primitive'
          }`}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
          placeholder={meta.placeholder}
          type="text"
          value={value}
        />
      </Hitarea>
    );
  },
  renderSummary: ({expr: _expr}: {expr: ContextualExpression}) => {
    // We have nothing that would show single elements in a list currently, once
    // we do we can improve this logic.
    return <span>Element</span>;
  },
  system: {
    defaultValue: [],
    displayName: 'Text',
    label: 'Text',
  },
  icon: TypeImplementation.IconByName(
    qualify(pluginName('Basic'), iconName('Text'))
  ),
});
